import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { fnGetWhInv } from "@shared/utils/InvUtils";
import {
  applyAutoSlip,
  getInitFormData,
  fnGetIotype,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { RowNode } from "ag-grid-community";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import * as converter from "@shared/utils/ValueConverterUtils";

interface Props {
  Menuprops: menuInfo;
}

// 출고요청등록
const SN_MTL_IRQ_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const { userInfo, config } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);
  const isFirstRender = useRef(true); // 조회조건 전표유형 관련 로직이 돌았는지
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");

  const [mas8025, setMAS8025] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });

        setMAS8025(config?.MAS8025?.dc_rmk ?? "");
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "2", "", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  // 전표유형 조회조건 바뀌면 출고형태, 보이는 컬럼 변경
  const onSearchPanelValueChanged = (event: ValueChangedEvent) => {
    const { colId, newValue } = event;

    switch (colId) {
      case "tp_slip":
        isFirstRender.current = false;
        const virtual = setVisibleColums(columns.grid1, newValue);
        getGridCurrent("grid1").changeColumnReorderConfigs(virtual);
        break;
    }
  };

  const setVisibleColums = (gridColumns: any[], tpSlip: string) => {
    const columns: any[] = [];

    gridColumns.forEach((row: any) => {
      switch (row.cd_column) {
        case "cd_io":
          const cdIoList: any[] = [];

          iotypeRef.current.forEach((item) => {
            if (item.tp_slip === tpSlip) {
              if (
                (item.tp_slip === "5" && item.fg_map === "1") ||
                item.tp_slip !== "5"
              ) {
                cdIoList.push({ code: item.cd_io, name: item.nm_io });
              }
            }
          });

          const jsonResult = JSON.stringify(cdIoList, null);
          columns.push({
            cd_column: row.cd_column,
            fg_visible: "Y",
            no_seq: row.no_seq,
            js_combo: jsonResult,
          });
          break;
        case "cd_wh_ref":
          if (tpSlip === "5" || tpSlip === "7" || tpSlip === "6") {
            columns.push({
              cd_column: row.cd_column,
              fg_visible: "Y",
              no_seq: row.no_seq,
            });
          } else if (tpSlip === "6") {
            columns.push({
              cd_column: row.cd_column,
              fg_visible: mas8025 === "LUMIR" ? "Y" : "N",
              no_seq: row.no_seq,
            });
          } else {
            columns.push({
              cd_column: row.cd_column,
              fg_visible: "N",
              no_seq: row.no_seq,
            });
          }
          break;
        case "cd_biz":
        case "cd_biz_ref":
          if (tpSlip === "7") {
            columns.push({
              cd_column: row.cd_column,
              fg_visible: "Y",
              no_seq: row.no_seq,
            });
          } else {
            columns.push({
              cd_column: row.cd_column,
              fg_visible: "N",
              no_seq: row.no_seq,
            });
          }
          break;
        case "tp_trans":
          if (tpSlip === "6") {
            columns.push({
              cd_column: row.cd_column,
              fg_visible: "Y",
              no_seq: row.no_seq,
            });
          } else {
            columns.push({
              cd_column: row.cd_column,
              fg_visible: "N",
              no_seq: row.no_seq,
            });
          }
          break;
        case "cd_pjt":
        case "cd_pjt_ref":
          if (tpSlip === "6") {
            columns.push({
              cd_column: row.cd_column,
              fg_visible: mas8025 === "LUMIR" ? "Y" : "N",
              no_seq: row.no_seq,
            });
          }
          break;
        default:
          columns.push({
            cd_column: row.cd_column,
            fg_visible: row.fg_visible,
            no_seq: row.no_seq,
          });
          break;
      }
    });

    return [...columns];
  };

  //#region Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));
  //#endregion

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_irq_reg_001_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_req: nodeData.no_req,
        dt_from: nodeData.dt_req,
        dt_to: nodeData.dt_req,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      const searchInfo = searchRef.current.getFormdata();

      if (searchInfo.tp_slip === undefined || searchInfo.tp_slip === "") {
        showDialog("alter", "전표유형은 필수입니다.");
        return;
      }
      // 첫 렌더링 안돌아서 이렇게
      if (isFirstRender.current) {
        isFirstRender.current = false;
        const virtual = setVisibleColums(columns.grid1, "5");
        getGridCurrent("grid1").changeColumnReorderConfigs(virtual);
      }

      // cd_io top1로직
      const validItems = iotypeRef.current.filter((item) => {
        if (item.tp_slip !== searchInfo.tp_slip) return false;
        return (
          (item.tp_slip === "5" && item.fg_map === "1") || item.tp_slip !== "5"
        );
      });

      const cdIoTop1 = validItems.sort((a, b) =>
        a.cd_io.localeCompare(b.cd_io)
      )[0]?.cd_io;

      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", cdIoTop1);
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    let hasInvalid = false;
    // 안보이는 isureql항목 삭제
    getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
      if (node.data.qt_io !== 0) {
        hasInvalid = true;
      }
    });

    if (hasInvalid) {
      showDialog("alter", "이미 진행된 건이 있어 삭제할 수 없습니다.");
      return;
    }

    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    let hasInvalid = false;
    if (focusedRowNodeL.data.state !== "insert") {
      if (focusedRowNodeL.data.qt_io !== 0) {
        hasInvalid = true;
      }
    }
    if (hasInvalid) {
      showDialog("alter", "이미 진행된 건입니다.");
      return;
    }

    getGridCurrent("grid2").deleteSelectedRows();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const isureqhChanges = getGridCurrent("grid1").getChange();
      const isureqlChanges = getGridCurrent("grid2").getChange();

      const headerInserts = isureqhChanges.insert ?? [];
      const lineInserts = isureqlChanges.insert ?? [];

      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        isureqhChanges.modifiedRowcount === 0 &&
        isureqlChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      if (isureqhChanges.insert.length > 0) {
        const data = isureqhChanges.insert[0];
        switch (data.tp_slip) {
          case "5":
            if (converter.getString(data.cd_wh_ref) === "") {
              await showDialog("alter", "입고창고는 필수입니다.");
              return false;
            }
            break;
          case "6":
            if (converter.getString(data.tp_trans) === "") {
              await showDialog("alter", "대체유형은 필수입니다.");
              return false;
            }

            const ioType = iotypeRef.current.find(
              (item) => item.cd_io === data.cd_io
            );
            if (ioType.dc_rmk === "pjt") {
              if (converter.getString(data.cd_wh_ref) === "") {
                await showDialog("alter", "입고창고는 필수입니다.");
                return false;
              } else if (converter.getString(data.cd_pjt) === "") {
                await showDialog("alter", "출고프로젝트는 필수입니다.");
                return false;
              } else if (converter.getString(data.cd_pjt_ref) === "") {
                await showDialog("alter", "출고프로젝트는 필수입니다.");
                return false;
              } else if (data.cd_pjt === data.cd_pjt_ref) {
                await showDialog(
                  "alter",
                  "출고프로젝트와 입고프로젝트가 동일합니다."
                );
                return false;
              }
            }
            break;
          case "7":
            if (converter.getString(data.cd_wh_ref) === "") {
              await showDialog("alter", "입고창고는 필수입니다.");
              return false;
            }
            if (converter.getString(data.cd_biz_ref) === "") {
              await showDialog("alter", "입고사업장은 필수입니다.");
              return false;
            } else if (data.cd_biz === data.cd_biz_ref) {
              await showDialog(
                "alter",
                "출고사업장과 입고사업장이 동일합니다."
              );
              return false;
            }
            break;
        }
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_req",
        cd_module: "MTL",
        no_line: 10,
        dt: focusedRowNodeH.data.dt_req,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [isureqhChanges, isureqlChanges],
        cd_menu: Menuprops.cd_menu,
      }); // 요청 데이터 구성

      const SaveData = {
        isureqh: isureqhChanges,
        isureql: isureqlChanges,
      }; // 저장 API 호출

      const ok = await fnSave("SN_MTL_IRQ_REG_001_SAVE_01", SaveData);
      if (!ok) return false; // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo.cd_biz,
        cd_dept: userInfo.cd_dept,
        nm_dept: userInfo.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_req: today,
        fg_slip: "2",
        tp_slip: searchInfo?.tp_slip,
        st_req: "F",
        cd_wh: "",
        cd_biz_ref: searchInfo.tp_slip == "7" ? "" : null,
        no_req: "",
        yn_edit: "Y",
      };
      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io, "noEvent");
        rowNode.setDataValue("fg_money", ioType.fg_money, "noEvent");

        // 프로젝트 대체면
        if (ioType.tp_slip === "6") {
          if (ioType.dc_rmk === "pjt") {
            rowNode.setDataValue("yn_edit", "Y", "noEvent");
          } else {
            rowNode.setDataValue("yn_edit", "N", "noEvent");
            rowNode.setDataValue("cd_wh_ref", "", "noEvent");
            rowNode.setDataValue("nm_wh_ref", "", "noEvent");
            rowNode.setDataValue("cd_pjt", "", "noEvent");
            rowNode.setDataValue("nm_pjt", "", "noEvent");
            rowNode.setDataValue("cd_pjt_ref", "", "noEvent");
            rowNode.setDataValue("nm_pjt_ref", "", "noEvent");
          }
        }
        break;
      case "cd_wh":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.cd_wh, "noEvent");
        });
        break;
      case "cd_biz_ref":
        getGridCurrent("grid1").applyPopupConditions({
          cd_wh_ref: { cd_biz: rowData.cd_biz_ref },
        });
        break;
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_req = selectedRow.no_req;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_req);
    } else {
      return;
    }
  };
  //#endregion

  //#region GridL Events
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      if (focusedRowNodeH.data.st_req === "C") {
        showDialog("alter", "이미 마감된 건입니다.");
        return false;
      }

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: focusedRowNodeH.data?.cd_firm,
        no_line: maxNoLine + 1,
        no_req: focusedRowNodeH.data?.no_req,
        dt_dlv: today,
        qt: 0,
        qt_io: 0,
        cd_wh: focusedRowNodeH.data?.cd_wh,
        st_req: "F",
        qt_inv: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    switch (colId) {
      case "cd_item":
      case "cd_wh":
        if (
          rowData.cd_item !== "" &&
          rowData.cd_item !== null &&
          rowData.cd_item !== undefined
        ) {
          const data = {
            cd_biz: focusedRowNodeH.data.cd_biz,
            cd_item: rowData.cd_item,
            cd_wh: focusedRowNodeH.data.cd_wh,
          };
          const qt_inv = await fnGetWhInv(data);
          rowNode.setDataValue("qt_inv", qt_inv, "noEvent");
        } else {
          rowNode.setDataValue("qt_inv", 0, "noEvent");
        }
        break;
    }

    if (colId === "cd_pjt") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_pjt) return;

      let apply = false;
      getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
        if (converter.getString(node.data[colId]) === "") {
          apply = true;
        }
      });
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );

      if (confirm) {
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (converter.getString(node.data.cd_pjt) === "") {
            node.setDataValue("cd_pjt", rowData.cd_pjt);
            node.setDataValue("nm_pjt", rowData.nm_pjt);
          }
        });
      }
    }
  };

  // const handleContextMenuL = async (params: any) => {
  //   const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
  //   let displayedNodesL: any[] = [];
  //   getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
  //     displayedNodesL.push(node);
  //   });
  //   const selectedNodesL = displayedNodesL.filter(
  //     (node: any) => node?.data && node.isSelected()
  //   );
  //   let noLineString = "";
  //   const menuItems = useMenuStore.getState().menuItems;
  //   switch (params) {
  //     case "fn_sn_mtl_com_mti_reg_01":
  //       if (
  //         focusedNodeH.data.fg_slip === "2" &&
  //         focusedNodeH.data.tp_slip === "6"
  //       ) {
  //         selectedNodesL.forEach((row: any) => {
  //           noLineString += row.data.no_line + "|";
  //         });
  //         const returnData = [focusedNodeH.data.no_req, noLineString];
  //         const drillDown = menuItems
  //           ? menuItems.find(
  //               (item: any) => item?.cd_menu === "SN_MTL_MTI_REG_001"
  //             )
  //           : undefined;
  //         if (drillDown) {
  //           const filePath =
  //             `./${drillDown.cd_module}/${drillDown.cd_menu}/${drillDown.cd_menu}`.toUpperCase();
  //           (window as any).onActivate(
  //             drillDown.cd_menu, // 메뉴 키
  //             drillDown.nm_menu, // 메뉴 제목
  //             drillDown.cd_module, // 모듈
  //             filePath, // 파일 경로
  //             drillDown, // 데이터
  //             drillDown.nm_path,
  //             returnData,
  //             "fg_drilldown.Registration"
  //           );
  //         }
  //       } else {
  //         await showDialog(
  //           "alter",
  //           "저장실패! 전표유형을 확인하시기 바랍니다."
  //         );
  //         return;
  //       }
  //       break;
  //     case "fn_sn_mtl_com_mow_reg_02":
  //       if (
  //         focusedNodeH.data.fg_slip === "2" &&
  //         focusedNodeH.data.tp_slip === "5" &&
  //         focusedNodeH.data.fg_map === "2"
  //       ) {
  //         selectedNodesL.forEach((row: any) => {
  //           noLineString += row.data.no_line + "|";
  //         });
  //         const returnData = [focusedNodeH.data.no_req, noLineString];
  //         const drillDown = menuItems
  //           ? menuItems.find(
  //               (item: any) => item?.cd_menu === "SN_MTL_MOW_REG_002"
  //             )
  //           : undefined;
  //         if (drillDown) {
  //           const filePath =
  //             `./${drillDown.cd_module}/${drillDown.cd_menu}/${drillDown.cd_menu}`.toUpperCase();
  //           (window as any).onActivate(
  //             drillDown.cd_menu, // 메뉴 키
  //             drillDown.nm_menu, // 메뉴 제목
  //             drillDown.cd_module, // 모듈
  //             filePath, // 파일 경로
  //             drillDown, // 데이터
  //             drillDown.nm_path,
  //             returnData,
  //             "fg_drilldown.Registration"
  //           );
  //         }
  //       } else {
  //         await showDialog(
  //           "alter",
  //           "저장실패! 전표유형을 확인하시기 바랍니다."
  //         );
  //         return;
  //       }
  //       break;
  //     case "fn_sn_mtl_com_mow_reg_01":
  //       if (
  //         focusedNodeH.data.fg_slip === "2" &&
  //         focusedNodeH.data.tp_slip === "5" &&
  //         focusedNodeH.data.fg_map === "1"
  //       ) {
  //         selectedNodesL.forEach((row: any) => {
  //           noLineString += row.data.no_line + "|";
  //         });
  //         const returnData = [focusedNodeH.data.no_req, noLineString];
  //         const drillDown = menuItems
  //           ? menuItems.find(
  //               (item: any) => item?.cd_menu === "SN_MTL_MOW_REG_001"
  //             )
  //           : undefined;
  //         if (drillDown) {
  //           const filePath =
  //             `./${drillDown.cd_module}/${drillDown.cd_menu}/${drillDown.cd_menu}`.toUpperCase();
  //           (window as any).onActivate(
  //             drillDown.cd_menu, // 메뉴 키
  //             drillDown.nm_menu, // 메뉴 제목
  //             drillDown.cd_module, // 모듈
  //             filePath, // 파일 경로
  //             drillDown, // 데이터
  //             drillDown.nm_path,
  //             returnData,
  //             "fg_drilldown.Registration"
  //           );
  //         }
  //       } else {
  //         await showDialog(
  //           "alter",
  //           "저장실패! 전표유형을 확인하시기 바랍니다."
  //         );
  //         return;
  //       }
  //       break;
  //     case "wf_sn_close_line": // 마감처리
  //       for (const node of selectedNodesL) {
  //         if (node.data?.st_req !== "C") {
  //           node.setDataValue("st_req", "C");
  //         } else {
  //           await showDialog("error", "이미 마감처리된 건입니다.");
  //           return;
  //         }
  //         await onSave();
  //       }
  //       break;
  //     case "wf_sn_close_line_cancel": //마감취소
  //       for (const node of selectedNodesL) {
  //         if (node.data?.st_req === "C") {
  //           if (node.data?.qt_io > 0) {
  //             node.setDataValue("st_req", "S");
  //           } else if (node.data?.qt_io === 0) {
  //             node.setDataValue("st_req", "F");
  //           }
  //         } else {
  //           if (node.data?.st_req !== "C") {
  //             await showDialog("error", "이미 마감처리된 건입니다.");
  //             return;
  //           }
  //         }
  //         await onSave();
  //       }
  //       break;
  //     default:
  //   }
  // };

  const onGridLBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };
  //#endregion

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
          // onContextMenu: handleContextMenuL,
          onBtnAddClick: onGridLBtnsClick,
          onBtnDeleteClick: onGridLBtnsClick,
        },
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchPanelValueChanged}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "요청정보" }}
            events={grids.grid1.events}
            filter={{
              refs: [gridRefs.current["grid2"]],
              columns: ["no_req"],
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_IRQ_REG_001;
